import {myEnrollmentMessagesApi} from '@/api/my-enrollment-messages.api.js'
import {myChannelMessagesApi} from '@/api/my-channel-messages.api.js'
import {Enrollment} from '@/models/enrollment.model.js'
import {Channel} from '@/models/channel.model.js'
import {Message} from '@/models/message.model.js'
import {useUuid} from '@/composables/use-uuid.js'
import {useProvider} from '@/composables/use-provider.js'
import {ref, onUnmounted} from 'vue'

/**
 * Helper composable for enrollment messages querying in conversation view.
 * Pass either an enrollment ID or a channel ID and it will load the relevant
 * messages.
 */
export function useEnrollmentMessagesQuerying(channelId, enrollmentId) {

  //Provider detection
  const {currentProvider} = useProvider()

  //Refs
  const enrollment = ref(null)
  const channel = ref(null)
  const messages = ref([])
  const hasMoreMessages = ref(false)

  //Load enrollment messages
  const loadEnrollmentMessages = async () => {
    const data = await myEnrollmentMessagesApi.query(enrollmentId, {limit: 300})
    setEnrollment(data.meta?.enrollment)
    setMessages(data.messages, data.meta)
  }

  //Load channel messages
  const loadChannelMessages = async () => {
    const data = await myChannelMessagesApi.query(channelId, {
      limit: 300,
      provider: currentProvider.value,
    })
    setEnrollment(data.meta?.enrollment)
    setChannel(data.meta?.channel)
    setMessages(data.messages, data.meta)
  }

  //Force update messages ref
  const updateMessages = () => {
    messages.value = [...messages.value]
  }

  //Load messages
  const loadMessages = async () => {
    if (channelId) {
      await loadChannelMessages()
    }
    else if (enrollmentId) {
      await loadEnrollmentMessages()
    }
    else {
      throw new Error('No enrollment or channel ID provided')
    }
  }

  //Test a mock message coming in
  const testMockMessage = () => {
    const {uuid} = useUuid()
    const message = Message.createFrom({
      id: uuid.value,
      body: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. In sit amet elit vel nisi blandit ornare. Maecenas purus quam, dignissim vel quam at, pulvinar efficitur est. Integer egestas porta mauris, quis convallis odio finibus at. Pellentesque non tempor ante. Etiam elit nunc, mollis nec consequat non, dignissim nec lacus. Cras nisl lacus, consequat sit amet tempus vitae, varius eget lacus. Curabitur dui nisi, vestibulum eu volutpat at, tincidunt vitae urna. Vestibulum euismod tellus in imperdiet vehicula. Etiam eu tortor ac ante convallis sagittis.',
      sentAt: (new Date()).toISOString(),
      direction: 'outgoing',
    })
    setTimeout(() => setMessages([message], {total: 1}), 1000)
  }

  //Set enrollment
  const setEnrollment = data => {
    if (data) {
      enrollment.value = Enrollment.createFrom(data)
    }
  }

  //Set channel
  const setChannel = data => {
    if (data) {
      channel.value = Channel.createFrom(data)
    }
  }

  //Helper to set messages after loading
  const setMessages = (allMessages, meta) => {

    //Set the has more messages flag
    if (meta?.total > allMessages.length) {
      hasMoreMessages.value = true
    }

    //If there are no messages yet, set all of them directly
    if (messages.value.length === 0) {
      messages.value = allMessages
      return
    }

    //First check if there are any messages sent by the user which are confirmed
    //to have been received by the server and update them with ID, timestamp, etc.
    messages.value
      .filter(message => message.isNew && message.isIncoming && message.externalId)
      .forEach(message => {
        const match = allMessages.find(m => m.externalId === message.externalId)
        if (match) {
          Object.assign(message, match)
        }
      })

    //Now find out which messages are new
    const existingIds = messages.value.map(message => message.id)
    const newMessages = allMessages.filter(message => !existingIds.includes(message.id))

    //Add new messages to the array
    messages.value = [...messages.value, ...newMessages]
  }

  //Poll messages
  const pollMessagesInterval = ref(null)
  const startPollingMessages = () => {
    stopPollingMessages()
    pollMessagesInterval.value = setInterval(loadMessages, 10000)
  }
  const stopPollingMessages = () => {
    clearInterval(pollMessagesInterval.value)
    pollMessagesInterval.value = null
  }

  //Always stop polling when component is unmounted
  onUnmounted(stopPollingMessages)

  //Expose interface
  return {
    enrollment,
    messages,
    channel,
    loadMessages,
    updateMessages,
    startPollingMessages,
    stopPollingMessages,
    testMockMessage,
  }
}
